// domain/sparepart.ts — helper murni Supplier/Sparepart (port v0.18). Modal tiket =
// Σ nilai sparepart TERPAKAI (dari serah) request non-batal. Hutang supplier per-req.
export interface SerahItem { barcode: string; nama: string; qty: number; harga: number; ket?: string }
/** Status retur: pending (staf ajukan) → diterima (supplier setuju, part balik) / ditolak (supplier tolak, part tetap terpakai).
 *  Legacy (undefined) = retur lama instan (dianggap diterima). */
export type ReturStatus = 'pending' | 'diterima' | 'ditolak';
/** Satu peristiwa retur (all-or-nothing: items = seluruh serah yang dikembalikan). */
export interface ReturEvent { at?: string; by?: string; items: SerahItem[]; status?: ReturStatus; ket?: string; decidedBy?: string; decidedAt?: string }
export interface Serah { items: SerahItem[]; at?: string; by?: string; retur?: ReturEvent[] }
export interface ReqPayment { amount: number; method?: string; note?: string; by?: string; created_at?: string }
export interface SparepartReq {
  id: number; ticket_no: string; outlet: string | null; supplier_id: number | null; supplier_nama: string | null;
  items: string[]; serah: Serah | null; batal: boolean; created_at: string; payments?: ReqPayment[]; by_user?: string;
  /** Barang belum ada di supplier mana pun → dipesan (indent). ETA diisi supplier. */
  indent?: boolean; indent_eta?: string | null; indent_note?: string | null;
  /** Path foto part yang dicari (bucket 'sparepart') — membantu supplier mengenali barangnya. */
  foto?: string[];
  /** Titipan sesama cabang (0124): request turunan dari request supplier lain. */
  parent_req_id?: number | null; titip_ket?: string | null; tolak_ket?: string | null;
  /** Konfirmasi penerimaan teknisi (0128). null = barang diserahkan tapi BELUM diterima. */
  diterima_at?: string | null; diterima_by?: string | null; ingat_at?: string | null;
}

const n = (x: unknown): number => (typeof x === 'number' && !isNaN(x) ? x : 0);
const keyOf = (it: { barcode?: string; nama?: string }): string => it.barcode || it.nama || '';

/** Retur yang MENGURANGI terpakai = diterima supplier ATAU legacy (undefined status).
 *  Konservatif (keputusan Apo): 'pending' belum kurangi modal; 'ditolak' → part tetap terpakai. */
const returCounts = (r: ReturEvent): boolean => r.status !== 'pending' && r.status !== 'ditolak';

/** Qty yang sudah diretur (diterima) untuk 1 barang (match by barcode/nama). */
export function returnedQty(serah: Serah | null, key: string): number {
  if (!serah || !Array.isArray(serah.retur)) return 0;
  return serah.retur.filter(returCounts).reduce((a, r) => a + (r.items ?? []).reduce((b, it) => b + (keyOf(it) === key ? n(it.qty) : 0), 0), 0);
}

/** Peristiwa retur yang masih MENUNGGU konfirmasi supplier (untuk panel Terima/Tolak). */
export function pendingRetur(serah: Serah | null): ReturEvent | null {
  return serah?.retur?.find((r) => r.status === 'pending') ?? null;
}

/** Kerugian teknisi = nilai part yang retur-nya DITOLAK supplier (part tetap dibayar iFiCs, tak balik). */
export function rejectedLoss(serah: Serah | null): number {
  if (!serah || !Array.isArray(serah.retur)) return 0;
  return serah.retur.filter((r) => r.status === 'ditolak').reduce((a, r) => a + (r.items ?? []).reduce((b, it) => b + n(it.qty) * n(it.harga), 0), 0);
}

/** Barang TERPAKAI (net) = serah − yang diretur. Utk baris "✅ Terpakai" di histori. */
export function usedItems(serah: Serah | null): SerahItem[] {
  if (!serah || !Array.isArray(serah.items)) return [];
  return serah.items
    .map((it) => ({ ...it, qty: n(it.qty) - returnedQty(serah, keyOf(it)) }))
    .filter((x) => x.qty > 0);
}

/** Nilai sparepart yang DITERIMA/dipakai (dari serah) = hutang req ke supplier. */
export function serahTotal(serah: Serah | null): number {
  if (!serah || !Array.isArray(serah.items)) return 0;
  return serah.items.reduce((a, it) => a + n(it.qty) * n(it.harga), 0);
}

/**
 * Sudah diakui secara keuangan? Barang yang diserahkan tapi belum dikonfirmasi teknisi BELUM
 * masuk modal maupun hutang (keputusan Apo 30 Jul): barang yang belum diterima belum pasti ada,
 * dan kalau salah kirim, HPP/profit/komisi sudah tercemar padahal barangnya tak pernah dipakai.
 */
export const sudahDiterima = (r: SparepartReq): boolean => !!r.serah && !!r.diterima_at && !r.batal;

/** Modal (HPP) tiket = Σ nilai serah dari request non-batal yang sudah DITERIMA teknisi. */
export function modalFromRequests(reqs: SparepartReq[]): number {
  return reqs.reduce((a, r) => a + (sudahDiterima(r) ? serahTotal(r.serah) : 0), 0);
}

export function paidTotal(payments: ReqPayment[] | undefined): number {
  return (payments ?? []).reduce((a, p) => a + n(p.amount), 0);
}
/**
 * Bagian pembayaran yang JATUH dalam rentang tanggal — untuk Arus Kas (metode langsung).
 * Bukan `paidTotal`: yang menentukan kas keluar adalah TANGGAL BAYAR-nya, bukan tanggal serah part
 * atau tanggal unit diambil. `from`/`to` null = tanpa batas di sisi itu. Pembayaran tanpa tanggal
 * dilewati — tak bisa ditempatkan di periode mana pun tanpa menebak.
 */
export function paidInRange(payments: ReqPayment[] | undefined, from: string | null, to: string | null): number {
  return (payments ?? []).reduce((a, p) => {
    const tgl = (p.created_at ?? '').slice(0, 10);
    if (!tgl || (from && tgl < from) || (to && tgl > to)) return a;
    return a + n(p.amount);
  }, 0);
}
export function outstanding(usedTotal: number, paid: number): number {
  return Math.max(0, usedTotal - paid);
}
export function payStatus(usedTotal: number, paid: number): 'none' | 'belum' | 'sebagian' | 'lunas' {
  if (usedTotal <= 0) return 'none';
  if (usedTotal - paid <= 0) return 'lunas';
  return paid > 0 ? 'sebagian' : 'belum';
}

/** Jeda antar-penagihan supplier (keputusan Apo): satu tagihan gabungan, paling sering 3 hari sekali. */
export const JEDA_TAGIH_HARI = 3;

/**
 * Boleh menagih iFiCs sekarang? Dipakai untuk menonaktifkan tombol + memberi alasannya di layar;
 * penegakan sebenarnya ada di RPC `tagih_hutang_supplier` (klien bisa dilewati).
 * Stempel tak terbaca → dianggap belum pernah menagih: memblokir supplier selamanya karena satu
 * nilai rusak jauh lebih merugikan daripada satu notifikasi berlebih.
 */
export function bisaTagih(tagihAtISO: string | null | undefined, sisaPiutang: number, nowMs = Date.now()): { boleh: boolean; sisaHari: number } {
  if (n(sisaPiutang) <= 0) return { boleh: false, sisaHari: 0 };
  const t = tagihAtISO ? Date.parse(tagihAtISO) : NaN;
  if (isNaN(t)) return { boleh: true, sisaHari: 0 };
  const lewatHari = (nowMs - t) / 86_400_000;
  if (lewatHari >= JEDA_TAGIH_HARI) return { boleh: true, sisaHari: 0 };
  return { boleh: false, sisaHari: Math.max(1, Math.ceil(JEDA_TAGIH_HARI - lewatHari)) };
}
